import React from 'react';
import styled from 'styled-components';
import { Body5, Container } from '@ui';
import { FlatButton, TransparentFlatButton } from '@ui/buttons';
import { IconButton } from '@ui/buttons/IconButton';
import { Working } from '@ui/Working';
import { QuerySeriesContext } from '@/contexts/QuerySeriesContext';
import { QuestionInput } from '@features/query/form/QuestionInput';
import Delete from '@assets/icons/svgs/Delete';
import { QuerySeriesResultsTable } from './QuerySeriesResultsTable';
import { RowChatProvider } from './chat/RowChatContext';
import { RowChatPanel } from './chat/RowChatPanel';
import { downloadResultsDoc } from './utils/downloadResultsDoc';

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const QuestionsListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const QuestionRowWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const QuestionInputWrapper = styled.div`
  flex: 1;

  textarea {
    height: 48px;
    min-height: 48px;
  }
`;

const ActionButtonsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  text-align: center;
`;

const ErrorWrapper = styled.div`
  color: ${({ theme }) => theme.themeColors.error || theme.themeColors.text};
`;

const QuerySeriesForm = () => {
  const { questions, setQuestions, submitSeries, isSubmittingToApi, formError } = React.useContext(QuerySeriesContext);

  const handleQuestionChange = React.useCallback(
    (index, value) => {
      const next = [...questions];
      next[index] = { question: value, displayQuestion: value };
      setQuestions(next);
    },
    [questions, setQuestions]
  );

  const handleAddQuestion = () => {
    setQuestions([...questions, { question: '', displayQuestion: '' }]);
  };

  const handleRemoveQuestion = (index) => {
    if (questions.length === 1) return;
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    submitSeries({ questions });
  };

  return (
    <FormWrapper>
      <QuestionsListWrapper>
        <Body5 bold>Questions</Body5>
        {questions.map((q, index) => (
          <QuestionRowWrapper key={`question-${index}`}>
            <QuestionInputWrapper>
              <QuestionInput
                value={q.displayQuestion}
                onChange={(event) => handleQuestionChange(index, event.target.value)}
              />
            </QuestionInputWrapper>
            {questions.length > 1 && (
              <IconButton onClick={() => handleRemoveQuestion(index)}>
                <Delete width={18} />
              </IconButton>
            )}
          </QuestionRowWrapper>
        ))}
        <TransparentFlatButton onClick={handleAddQuestion} disabled={isSubmittingToApi}>
          + Add Question
        </TransparentFlatButton>
      </QuestionsListWrapper>

      {formError && (
        <ErrorWrapper>
          <Body5>{formError}</Body5>
        </ErrorWrapper>
      )}
      {isSubmittingToApi && <Working />}
      <ActionButtonsWrapper>
        <FlatButton fullWidth onClick={handleSubmit} disabled={isSubmittingToApi}>
          Submit
        </FlatButton>
      </ActionButtonsWrapper>
    </FormWrapper>
  );
};

const ResultActionsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
`;

const QuerySeriesResults = () => {
  const { rows, resetSeries, isStreaming, isSubmittingToApi } = React.useContext(QuerySeriesContext);

  if (isSubmittingToApi) {
    return <Working />;
  }

  const handleDownload = () => {
    downloadResultsDoc(rows);
  };

  return (
    <Container>
      <QuerySeriesResultsTable />
      <Container top={24}>
        <ResultActionsWrapper>
          <FlatButton onClick={handleDownload} disabled={isStreaming}>
            Download
          </FlatButton>
          <TransparentFlatButton onClick={resetSeries} disabled={isStreaming}>
            Clear
          </TransparentFlatButton>
        </ResultActionsWrapper>
      </Container>
    </Container>
  );
};

export const QuerySeriesMode = ({ goBackOverrideRef }) => {
  const { rows, isSubmittingToApi, resetSeries } = React.useContext(QuerySeriesContext);
  const hasResults = rows.length > 0 || isSubmittingToApi;

  React.useEffect(() => {
    if (goBackOverrideRef) {
      goBackOverrideRef.current = hasResults ? resetSeries : null;
    }
  }, [hasResults, resetSeries, goBackOverrideRef]);

  if (hasResults) {
    return (
      <RowChatProvider>
        <QuerySeriesResults />
        <RowChatPanel />
      </RowChatProvider>
    );
  }

  return <QuerySeriesForm />;
};
